import { useState, useEffect, useRef } from 'react';
import './Education.css';
import COLLEGE_SCHOOL from '../COLLEGE.jpg';
import SHS_SCHOOL from '../SHS.webp';
import HIGHSCHOOL_SCHOOL from '../HIGHSCHOOL.jpeg';
import ELEM_SCHOOL from '../ELEM.jpeg';
import COLLEGE_UNIFORM from '../cover/college pic.jpg';
import SHS_UNIFORM from '../cover/fidelis pic.jpg';
import HIGHSCHOOL_UNIFORM from '../cover/junior pic.jpg';
import ELEM_UNIFORM from '../cover/elem pic.jpg';

function Education() {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.dataset.section]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    sectionRefs.current.forEach((ref) => { if (ref) observer.observe(ref); });
    return () => observer.disconnect();
  }, []);
  const currentSchool = {
    school: 'FAITH Colleges',
    program: 'Bachelor of Science in Computer Engineering',
    yearLevel: '3rd Year College',
    duration: '2023 - Present',
    focus: 'Hardware & Software Systems',
    status: 'Currently Enrolled',
    schoolImage: COLLEGE_SCHOOL,
    uniformImage: COLLEGE_UNIFORM,
    achievements: "Dean's Lister (2024–2026) | President's Lister (2023)"
  };

  const previousSchools = [
    {
      school: 'FAITH Fidelis Senior High',
      program: 'STEM Strand',
      gradeLevel: 'Grade 11 & 12',
      duration: '2021 - 2023',
      schoolImage: SHS_SCHOOL,
      uniformImage: SHS_UNIFORM,
      achievements: "Graduated With Honors"
    },
    {
      school: 'San Pedro National High',
      program: 'Junior High School',
      gradeLevel: 'Grade 7 - 10',
      duration: '2017 - 2021',
      schoolImage: HIGHSCHOOL_SCHOOL,
      uniformImage: HIGHSCHOOL_UNIFORM,
      achievements: "Graduated With Honors"
    },
    {
      school: 'Sto. Tomas North Central',
      program: 'Elementary',
      gradeLevel: 'Grade 1 - 6',
      duration: '2011 - 2017',
      schoolImage: ELEM_SCHOOL,
      uniformImage: ELEM_UNIFORM,
      achievements: "Graduated With Honors"
    }
  ];

  const courses = [
    'Data Structures & Algorithms',
    'Object-Oriented Programming',
    'Digital Systems Design',
    'Web Development',
    'Embedded Systems',
    'HDL / Verilog',
    'Signal Processing (DSP)',
    'Circuit Design & PCB',
    'Discrete Mathematics'
  ];

  const interests = [
    'Software Development',
    'Embedded Systems Engineering',
    'Digital Design & Microcontrollers',
    'Internet of Things (IoT)'
  ];

  return (
    <section id="education" className="education-page">
      {/* Header */}
      <div
        className={`education-header ${visibleSections.has('header') ? 'visible' : ''}`}
        data-section="header"
        ref={(el) => (sectionRefs.current[0] = el)}
      >
        <div className="header-bg-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
        <div className="education-header-content">
          <h1 className="page-title">Educational Background</h1>
          <p className="page-subtitle">My academic history, current pursuits, and specialization interests</p>
        </div>
      </div>

      <div className="education">
        <div className="education-container">
          
          {/* HERO SECTION FOR CURRENT SCHOOL */}
        <div className="current-school-hero">
          <div className="hero-text-content">
            <div className="hero-status-tag">{currentSchool.status}</div>
            <h5 className="hero-subtitle">CURRENT PURSUIT</h5>
            <h3 className="hero-title">{currentSchool.school}</h3>
            <h4 className="hero-program">{currentSchool.program}</h4>
            
            <div className="hero-meta-grid">
              <div className="hero-meta-item">
                <span className="hero-meta-label">Year Level</span>
                <span className="hero-meta-value">{currentSchool.yearLevel}</span>
              </div>
              <div className="hero-meta-item">
                <span className="hero-meta-label">Timeline</span>
                <span className="hero-meta-value">{currentSchool.duration}</span>
              </div>
              <div className="hero-meta-item">
                <span className="hero-meta-label">Academic Focus</span>
                <span className="hero-meta-value">{currentSchool.focus}</span>
              </div>
            </div>

            <div className="hero-achievements">
              <span className="hero-achieve-icon">🏆</span>
              <span className="hero-achieve-text">{currentSchool.achievements}</span>
            </div>
          </div>
          
          <div className="hero-visuals">
            <img src={currentSchool.schoolImage} alt={`${currentSchool.school} Campus`} className="hero-school-img" />
            <img src={currentSchool.uniformImage} alt="Jasmin in College Uniform" className="hero-uniform-img" />
          </div>
        </div>

        {/* PREVIOUS SCHOOLS GRID */}
        <div className="previous-schools-wrapper">
          <h3 className="sub-section-title">Previous Education</h3>
          <div className="previous-schools-grid">
            {previousSchools.map((school, index) => (
               <div key={index} className="prev-school-profile-card">
                 <div className="prev-visuals">
                   <img src={school.schoolImage} alt={`${school.school} Campus`} className="prev-school-img" />
                   <img src={school.uniformImage} alt="Uniform" className="prev-uniform-img" />
                   <div className="prev-overlay-duration">
                     <span>{school.duration}</span>
                   </div>
                 </div>

                 <div className="prev-details">
                   <h4 className="prev-school-name">{school.school}</h4>
                   <p className="prev-program">{school.program}</p>
                   <div className="prev-grade-badge">
                     <span>Grade Level:</span> {school.gradeLevel}
                   </div>
                   <p className="prev-achieve">{school.achievements}</p>
                 </div>
               </div>
            ))}
          </div>
        </div>

        {/* ISOLATED RELEVANT COURSEWORK SECTION */}
        <div className="separated-section coursework-section">
          <h3 className="separated-title">Relevant Coursework</h3>
          <div className="coursework-cards-grid">
            {courses.map((course, index) => (
              <div key={index} className="course-card-v2">
                <div className="course-card-accent"></div>
                <span className="course-card-text">{course}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ISOLATED SPECIALIZATION INTERESTS SECTION */}
        <div className="separated-section interests-section">
          <h3 className="separated-title">Specialization Interests</h3>
          <div className="interests-cards-row">
            {interests.map((interest, index) => (
              <div key={index} className="interest-card-v2">
                <div className="interest-icon-circle">
                  ✦
                </div>
                <span className="interest-card-text">{interest}</span>
              </div>
            ))}
          </div>
        </div>

        </div>

      </div>
    </section>
  );
}

export default Education;
